"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export default function PartnerCarousel({
  logos,
  speed = 90,
  gap = 64,
  title = "Trusted by leading brands & businesses worldwide",
  colour = "white",
  fadeColour = "accent",
  dim = true,
}) {
  const firstStripRef = useRef(null);
  const [scrollDistance, setScrollDistance] = useState(0);
  const fadeColourClass =
    fadeColour === "white"
      ? "before:from-white after:from-white"
      : "before:from-accent after:from-accent";
  const dimClass = dim ? "brightness-90" : "";

  useEffect(() => {
    const node = firstStripRef.current;

    if (!node) {
      return undefined;
    }

    const updateDistance = () => {
      setScrollDistance(node.offsetWidth);
    };

    updateDistance();

    const resizeObserver = new ResizeObserver(updateDistance);
    resizeObserver.observe(node);
    window.addEventListener("resize", updateDistance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDistance);
    };
  }, [logos, gap]);

  const duration = scrollDistance > 0 ? scrollDistance / Math.max(speed, 1) : 1;

  return (
    <div className="w-full  py-5 sm:py-6 ">
      {}

      <div className={`relative overflow-hidden ${dimClass} before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-16 before:bg-linear-to-r before:to-transparent before:content-[''] after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-16 after:bg-linear-to-l after:to-transparent after:content-[''] ${fadeColourClass}`}>
        <div
          className="marquee-track flex w-max items-center will-change-transform"
          style={{
            "--marquee-duration": `${duration}s`,
            "--marquee-distance": `${scrollDistance}px`,
          }}
        >
          {[...new Array(2)].fill(0).map((_, duplicateIndex) => (
            <div
              key={duplicateIndex}
              ref={duplicateIndex === 0 ? firstStripRef : null}
              className="flex flex-none items-center"
              style={{ gap: `${gap}px`, paddingRight: `${gap}px` }}
              aria-hidden={duplicateIndex === 1 ? "true" : undefined}
            >
              {logos.map((logo) => (
                <div
                  key={`${logo.src}-${duplicateIndex}`}
                  className="flex flex-none items-center justify-center opacity-80 transition-opacity duration-300 hover:opacity-100"
                >
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={logo.width}
                    height={logo.height}
                    className="flex-none object-contain"
                    style={{
                      width: `${logo.width}px`,
                      height: `${logo.height}px`,
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .marquee-track {
          animation: marquee-scroll var(--marquee-duration) linear infinite;
        }

        .marquee-track:hover {
          animation-play-state: paused;
        }

        @keyframes marquee-scroll {
          from {
            transform: translate3d(0, 0, 0);
          }

          to {
            transform: translate3d(calc(var(--marquee-distance) * -1), 0, 0);
          }
        }
      `}</style>
    </div>
  );
}
